import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { SxProps } from "@mui/material/styles";
import {
  ChannelSchema,
  OrgSourceSchema,
  SignableProductSchema,
} from "@polifonia/interlude/models";
import type { muiTheme } from "@polifonia/theme";
import { NumberStringSchema } from "@polifonia/utils/zod/NumberStringSchema";
import { createRootRoute, createRoute, Navigate } from "@tanstack/react-router";
import { z } from "zod";

import { useEnableBillingV2 } from "@/features/billing/useEnableBillingV2";
import { useEnableEmailChannelSelfOnboarding } from "@/features/email/useEnableEmailChannelSelfOnboarding";
import { useEnableMcpConnections } from "@/features/mcp/useEnableMcpConnections";
import { useEnableOpenApiV2KeySelfServe } from "@/features/openapi/useEnableOpenApiV2KeySelfServe";
import { useEnableVibeLab } from "@/features/vibe-lab/useEnableVibeLab";
import { ErrorFallbackPage } from "@/layout/ErrorBoundary/ErrorFallbackPage/OldErrorFallbackPage";
import { ApplicationsPage } from "@/pages/applications";
import { ApiKeysPage } from "@/pages/applications/pages/apiKeys";
import { CdhPage } from "@/pages/applications/pages/cdh";
import { CustomFieldsPage } from "@/pages/applications/pages/customFields";
import { GoogleAdsIntegrationPageWrapper } from "@/pages/applications/pages/googleAdsIntegration/GoogleAdsIntegrationPageWrapper";
import { LineAdsIntegrationPageWrapper } from "@/pages/applications/pages/lineAdsIntegration/LineAdsIntegrationPageWrapper";
import { McpConnectionsPage } from "@/pages/applications/pages/mcpConnections";
import { MetaAdsIntegrationPageWrapper } from "@/pages/applications/pages/metaAdsIntegration/MetaAdsIntegrationPageWrapper";
import { ProductFeedsPage } from "@/pages/applications/pages/productFeeds";
import { AddFeedPage } from "@/pages/applications/pages/productFeeds/pages/add";
import { FeedDetailPage } from "@/pages/applications/pages/productFeeds/pages/detail";
import { BillingPage } from "@/pages/billing";
import { BillingV2Page } from "@/pages/billing/v2";
import { ChannelsPage } from "@/pages/channels";
import { channelsSearchSchema } from "@/pages/channels/channelsSearchSchema";
import { ChannelDetailPage } from "@/pages/channels/pages/detail";
import { WebChatConfigPage } from "@/pages/channels/pages/detail/wccs/WebChatConfig";
import { WebStreamConfigPage } from "@/pages/channels/pages/detail/wccs/WebStreamConfig";
import { EmailOnboardPage } from "@/pages/channels/pages/emailOnboard";
import { PageLayout } from "@/pages/components/PageLayout";
import { ContractCenterPage } from "@/pages/contractCenter";
import { ContractSigningPage } from "@/pages/contractSigning";
import { RootComponent } from "@/pages/RootComponent";
import { UserManagementPage } from "@/pages/userManagement";
import { VibeLabPage } from "@/pages/vibeLab";

const styles = {
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    py: "48px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const tokenSearchSchema = z.object({
  token: z.string().or(z.undefined()),
  source: z.enum(["maac"]).or(z.undefined()),
});

const rootRoute = createRootRoute({
  validateSearch: tokenSearchSchema,
  component: () => <RootComponent />,
  notFoundComponent: () => <RootComponent />,
  errorComponent: (props: { error: Error }) => (
    <ErrorFallbackPage
      resetErrorBoundary={() => {}}
      error={props.error}
      reloadWindow
    />
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <ApplicationsPage />,
});

const contractSigningSearchSchema = z.object({
  // Never defaulted: product decides which contract the customer signs, and a
  // contract has legal effect, so a missing or unrecognized value must not
  // quietly resolve to some product's agreement. It still catches to undefined
  // rather than throwing, so a malformed link redirects (see
  // ContractSigningPage) instead of rendering an error page.
  product: SignableProductSchema.optional().catch(undefined),
  // Provided by the verdict redirect; re-validated in SigningFlow against the
  // first-party https allowlist before it is ever navigated to.
  return_url: z.string().optional(),
});

const contractSigningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contract-signing",
  validateSearch: contractSigningSearchSchema,
  component: () => <ContractSigningPage />,
});

const contractCenterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contract-center",
  component: () => <ContractCenterPage />,
});

const applicationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications",
  component: () => <ApplicationsPage />,
});

const cdhRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/cdh",
  component: () => <CdhPage />,
});

const googleAdsIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/google-ads-integration",
  component: () => <GoogleAdsIntegrationPageWrapper />,
});

const googleAdsIntegrationConversionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/google-ads-integration/conversions",
  component: () => <GoogleAdsIntegrationPageWrapper />,
});

const metaAdsIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/meta-ads-integration",
  component: () => <MetaAdsIntegrationPageWrapper />,
});

const lineAdsIntegrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/line-ads-integration",
  component: () => <LineAdsIntegrationPageWrapper />,
});

const productFeedsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/product-feeds",
  component: () => <ProductFeedsPage />,
});

const customFieldsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/custom-fields",
  component: () => <CustomFieldsPage />,
});

const ApiKeysPageWrapper: React.FC = function ApiKeysPageWrapper() {
  const enableOpenApiV2KeySelfServe = useEnableOpenApiV2KeySelfServe();
  if (!enableOpenApiV2KeySelfServe) {
    return <Navigate to="/applications" replace />;
  }
  return <ApiKeysPage />;
};

/**
 * The path is a cross-repo contract: MAAC links into Admin Center at
 * `applications/api-keys`. Do not rename it.
 */
const apiKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/api-keys",
  component: () => <ApiKeysPageWrapper />,
});

const McpConnectionsPageWrapper: React.FC =
  function McpConnectionsPageWrapper() {
    const enableMcpConnections = useEnableMcpConnections();
    if (!enableMcpConnections) {
      return <Navigate to="/applications" replace />;
    }
    return <McpConnectionsPage />;
  };

const mcpConnectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/mcp-connections",
  component: () => <McpConnectionsPageWrapper />,
});

const addFeedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/product-feeds/add",
  component: () => <AddFeedPage />,
});

const feedDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/applications/product-feeds/$feedId",
  params: {
    parse: (params) => ({
      feedId: z.coerce.number().parse(params.feedId),
    }),
  },
  component: () => <FeedDetailPage />,
});

const BillingPageWrapper: React.FC = function BillingPageWrapper() {
  const billingV2 = useEnableBillingV2();

  if (billingV2.isPending) {
    return (
      <PageLayout>
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!billingV2.isEnabled) return <BillingPage />;

  return <BillingV2Page />;
};

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  validateSearch: z.object({
    tab: z
      .enum([
        "overview",
        "feature-pricing",
        "topup-history",
        "transaction-history",
        "alert-setting",
      ])
      .optional(),
  }),
  component: () => <BillingPageWrapper />,
});

const channelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/channels",
  validateSearch: channelsSearchSchema,
  component: ChannelsPage,
});

const channelDetailRouteSchema = z.object({
  type: ChannelSchema.shape.type,
  id: z.union([NumberStringSchema, z.string().uuid()]),
});

const channelDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "channels/$type/$id",
  params: {
    parse: channelDetailRouteSchema.parse,
  },
  errorComponent: ChannelsPage,
  component: ChannelDetailPage,
});

const EmailOnboardPageWrapper: React.FC = function EmailOnboardPageWrapper() {
  const enableEmailChannelSelfOnboarding =
    useEnableEmailChannelSelfOnboarding();
  if (!enableEmailChannelSelfOnboarding) {
    return <Navigate to="/channels" replace />;
  }
  return <EmailOnboardPage />;
};

const emailOnboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/channels/email-onboard",
  component: () => <EmailOnboardPageWrapper />,
});

const webChatConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "channels/wccs/$id/web-chat-config",
  params: {
    parse: (params) => ({
      id: z.union([NumberStringSchema, z.string().uuid()]).parse(params.id),
    }),
  },
  errorComponent: ChannelsPage,
  component: WebChatConfigPage,
});

const webStreamConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "channels/wccs/$id/web-stream-config",
  params: {
    parse: (params) => ({
      id: z.union([NumberStringSchema, z.string().uuid()]).parse(params.id),
    }),
  },
  errorComponent: ChannelsPage,
  component: WebStreamConfigPage,
});

const userManagementRoute = createRoute({
  validateSearch: z
    .object({
      "target-edit-user-email": z.string().optional(),
    })
    .and(
      z.object({
        tab: OrgSourceSchema.optional(),
      }),
    ),
  getParentRoute: () => rootRoute,
  path: "/user-management",
  component: UserManagementPage,
});

const VibeLabPageWrapper: React.FC = function VibeLabPageWrapper() {
  const enableVibeLab = useEnableVibeLab();
  if (!enableVibeLab) {
    return <Navigate to="/applications" replace />;
  }
  return <VibeLabPage />;
};

const vibeLabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/vibe-lab",
  component: () => <VibeLabPageWrapper />,
});

export const routeTree = rootRoute.addChildren([
  applicationRoute,
  billingRoute,
  contractCenterRoute,
  contractSigningRoute,
  cdhRoute,
  customFieldsRoute,
  apiKeysRoute,
  mcpConnectionsRoute,
  googleAdsIntegrationRoute,
  googleAdsIntegrationConversionsRoute,
  metaAdsIntegrationRoute,
  lineAdsIntegrationRoute,
  productFeedsRoute,
  addFeedRoute,
  feedDetailRoute,
  channelRoute,
  channelDetailRoute,
  emailOnboardRoute,
  webChatConfigRoute,
  webStreamConfigRoute,
  userManagementRoute,
  vibeLabRoute,
  indexRoute,
]);
